/**
 * WriteoffSlice Core Reducers
 * 
 * Core CRUD operations for the writeoff entity.
 */

import { PayloadAction, CaseReducer } from '@reduxjs/toolkit';
import { WriteoffView } from '../../types';
import { WriteoffSliceState } from '../state';
import { addEditedField, createBackupManager } from '../../../shared/sliceUtils';
import { createDefaultWriteoff } from '../../createDefaultWriteoff';

// Backup manager for HMR recovery
const backupManager = createBackupManager<{
  writeoff: WriteoffView;
  isEdited: boolean;
  editedFields: string[];
}>('writeoff_backup');

/**
 * Helper to persist state to session storage
 */
function persistToSessionStorage(state: WriteoffSliceState) {
  if (state.currentWriteoff) {
    backupManager.persist({
      writeoff: state.currentWriteoff,
      isEdited: state.isEdited,
      editedFields: state.editedFields,
    });
  }
}

// =============================================================================
// CORE REDUCERS
// =============================================================================

/**
 * Main update action - updates writeoff with partial data
 */
export const updateWriteoff: CaseReducer<WriteoffSliceState, PayloadAction<Partial<WriteoffView>>> = (
  state,
  action
) => {
  if (state.currentWriteoff) {
    state.currentWriteoff = { ...state.currentWriteoff, ...action.payload };
    state.isEdited = true;
    // Add all updated fields to editedFields
    Object.keys(action.payload).forEach((field) => {
      if (!state.editedFields.includes(field)) {
        state.editedFields.push(field);
      }
    });
  } else {
    // If no current writeoff, set it directly from payload
    state.currentWriteoff = action.payload as WriteoffView;
    state.isEdited = false;
    state.editedFields = [];
  }
  persistToSessionStorage(state);
};

/**
 * Set the current writeoff (from API response or navigation)
 */
export const setCurrentWriteoff: CaseReducer<WriteoffSliceState, PayloadAction<WriteoffView | null>> = (
  state,
  action
) => {
  state.currentWriteoff = action.payload;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/**
 * Update current writeoff with partial data (legacy compatibility)
 */
export const updateCurrentWriteoff: CaseReducer<WriteoffSliceState, PayloadAction<Partial<WriteoffView>>> = (
  state,
  action
) => {
  if (state.currentWriteoff) {
    state.currentWriteoff = { ...state.currentWriteoff, ...action.payload };
    addEditedField(state, 'writeoff');
    persistToSessionStorage(state);
  }
};

/**
 * Create a new default writeoff
 */
export const createNewWriteoff: CaseReducer<
  WriteoffSliceState,
  PayloadAction<{ company?: string; center?: string; caseId?: string }>
> = (state, action) => {
  const defaultWriteoff = createDefaultWriteoff();
  if (action.payload) {
    defaultWriteoff.identifiers.company = action.payload.company || '';
    defaultWriteoff.identifiers.center = action.payload.center || '';
    defaultWriteoff.identifiers.caseId = action.payload.caseId || '';
  }
  state.currentWriteoff = defaultWriteoff;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/**
 * Clear the current writeoff
 */
export const clearCurrentWriteoff: CaseReducer<WriteoffSliceState> = (state) => {
  state.currentWriteoff = null;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
};

/**
 * Force reset writeoff even with unsaved changes
 */
export const forceResetWriteoff: CaseReducer<WriteoffSliceState> = (state) => {
  state.currentWriteoff = null;
  state.isEdited = false;
  state.editedFields = [];
  state.validationErrors = {};
  backupManager.clear();
};

// =============================================================================
// BACKUP/RESTORE REDUCERS
// =============================================================================

/**
 * Restore from session storage backup (for HMR)
 */
export const restoreFromBackup: CaseReducer<WriteoffSliceState> = (state) => {
  const backup = backupManager.restore();
  if (backup) {
    state.currentWriteoff = backup.writeoff;
    state.isEdited = backup.isEdited;
    state.editedFields = backup.editedFields;
  }
};

/**
 * Clear the session storage backup
 */
export const clearBackup: CaseReducer<WriteoffSliceState> = () => {
  backupManager.clear();
};
